import { html, render, type TemplateResult } from "lit-html";
import { store, type AppState } from "../data/store";
import { api } from "../data/api";
import type { BuyerRow, ConversationStatus, Message } from "../data/types";
import { ALL_STATUSES } from "../data/types";
import { formatRelative } from "../utils/time";
import { findConflictFor, classifyVenue } from "../utils/schedule";
import { extractPhones, formatPhone, imessageHref, normalizePhone } from "../utils/phone";
import { iconMessage, iconClipboard, iconPhone, iconCheck } from "./icons";

/**
 * EditorSheet — modal/sheet that opens when you click a buyer row.
 *
 * Sections:
 *   1. Conversation thread (lazy-loaded inbound + outbound messages)
 *   2. Reply composer (1000-char cap; sends via Resend → OfferUp)
 *   3. Deal terms (Tickets / Total $ / Per ticket — three-way math)
 *   4. Status pills (inquired / negotiating / committed / met / ghosted / declined)
 *   5. Meet spot
 *
 * "Mark as Paid" is its own prominent green button in the footer.
 */

type NumField = "tickets" | "total" | "perTicket";

interface LocalState {
  tickets:   string;
  total:     string;
  perTicket: string;
  status:    ConversationStatus;
  meet:      string;
  /** ISO-local datetime string for <input type="datetime-local">. "" if unset. */
  meetAt:    string;
  history:   NumField[];
  saving:    boolean;
  err:       string | null;

  // Buyer + conversation re-assignment (Phase 2.E)
  accountId: string;       // conversation.account_id
  phone:     string;       // buyer.phone (E.164 or user-typed)
  notes:     string;       // buyer.notes — private operator notes

  // Conversation thread + composer (Phase 2.C)
  messages:        Message[];
  loadingMessages: boolean;
  replyText:       string;
  sending:         boolean;
  sendErr:         string | null;

  /** Last buyer.conv_last_activity_at we observed. When the buyer
   *  row updates (via SSE → store.refresh), we compare and trigger
   *  a quiet reload of messages so the thread mirrors what's in D1
   *  without the operator having to close + reopen the sheet. */
  lastActivityKnown: number;
}

let local: LocalState | null = null;
let lastConvId: string | null = null;

/* ============== Body-scroll lock (iOS-safe) ============== */
// When the sheet is open, the underlying page must not scroll. iOS
// Safari ignores `overflow: hidden` on body, so we fix the body in
// place at the current scroll offset and restore on close.
let bodyLocked      = false;
let lockedScrollY   = 0;
function lockBody(): void {
  if (bodyLocked) return;
  lockedScrollY = window.scrollY;
  const b = document.body.style;
  b.position = "fixed";
  b.top      = `-${lockedScrollY}px`;
  b.left     = "0";
  b.right    = "0";
  document.body.classList.add("fest-sheet-open");
  bodyLocked = true;
}
function unlockBody(): void {
  if (!bodyLocked) return;
  const b = document.body.style;
  b.position = "";
  b.top      = "";
  b.left     = "";
  b.right    = "";
  document.body.classList.remove("fest-sheet-open");
  window.scrollTo(0, lockedScrollY);
  bodyLocked = false;
}

/* ============== Swipe-down dismiss ============== */
const SHEET_DISMISS_PX           = 120;     // drag this far → close
const SHEET_VELOCITY_PX_PER_MS   = 0.5;     // …or flick this fast
const GESTURES_MOUNTED_KEY       = "gesturesMounted";

interface DragState {
  startY:           number;
  lastY:            number;
  lastT:            number;
  velocityPxPerMs:  number;
  initialScrollTop: number;
  dragging:         boolean;
}

function mountSheetGestures(): void {
  const sheet   = document.querySelector<HTMLElement>("#editor-sheet-host .sheet");
  const overlay = document.querySelector<HTMLElement>("#editor-sheet-host .sheet-overlay");
  if (!sheet || !overlay) return;
  if (sheet.dataset[GESTURES_MOUNTED_KEY] === "1") return;
  sheet.dataset[GESTURES_MOUNTED_KEY] = "1";

  const scrollEl = sheet.querySelector<HTMLElement>(".sheet__body");
  if (!scrollEl) return;

  mountReplyFocusFix(scrollEl);

  // Only meaningful on touch devices. Mouse interactions just use the
  // close button or overlay click.
  if (!("ontouchstart" in window)) return;

  const drag: DragState = {
    startY: 0, lastY: 0, lastT: 0, velocityPxPerMs: 0,
    initialScrollTop: 0, dragging: false,
  };

  function reset(): void {
    drag.startY = 0; drag.lastY = 0; drag.velocityPxPerMs = 0; drag.dragging = false;
  }

  function snapBack(): void {
    if (!sheet) return;
    sheet.style.transition = "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)";
    sheet.style.transform  = "";
    if (overlay) overlay.style.background = "";
    setTimeout(() => {
      if (sheet) sheet.style.transition = "";
    }, 260);
  }

  function onTouchStart(ev: TouchEvent): void {
    if (ev.touches.length !== 1) return;
    const t = ev.touches[0]!;
    drag.startY            = t.clientY;
    drag.lastY             = t.clientY;
    drag.lastT             = performance.now();
    drag.velocityPxPerMs   = 0;
    drag.initialScrollTop  = scrollEl!.scrollTop;
    drag.dragging          = false;
  }

  function onTouchMove(ev: TouchEvent): void {
    if (drag.startY === 0) return;
    const t  = ev.touches[0]!;
    const y  = t.clientY;
    const dy = y - drag.startY;

    const now = performance.now();
    const dt  = now - drag.lastT;
    if (dt > 0) {
      const sample = (y - drag.lastY) / dt;
      drag.velocityPxPerMs = drag.velocityPxPerMs * 0.4 + sample * 0.6;
    }
    drag.lastY = y;
    drag.lastT = now;

    if (!drag.dragging) {
      // Decide: claim as sheet-drag OR let content scroll.
      // Only claim when scroll is at top AND user is dragging DOWN.
      if (drag.initialScrollTop > 0) return;
      if (scrollEl!.scrollTop > 0)   return;
      if (dy <= 0)                   return;
      drag.dragging = true;
    }

    if (ev.cancelable) ev.preventDefault();
    const offset = Math.max(0, dy);
    sheet!.style.transform = `translateY(${offset}px)`;
    if (overlay) {
      const fade  = Math.min(1, offset / (window.innerHeight * 0.5));
      const alpha = 0.55 * (1 - fade * 0.85);
      overlay.style.background = `rgba(0, 0, 0, ${alpha.toFixed(3)})`;
    }
  }

  function onTouchEnd(): void {
    if (!drag.dragging) { reset(); return; }
    const distance     = Math.max(0, drag.lastY - drag.startY);
    const flickFast    = drag.velocityPxPerMs > SHEET_VELOCITY_PX_PER_MS;
    const pastDistance = distance > SHEET_DISMISS_PX;
    if (pastDistance || flickFast) {
      // Animate out — slide the rest of the way down before closing.
      const remaining = window.innerHeight - distance;
      sheet!.style.transition = "transform 180ms cubic-bezier(0.4, 0, 0.2, 1)";
      sheet!.style.transform  = `translateY(${distance + remaining}px)`;
      if (overlay) overlay.style.background = "rgba(0, 0, 0, 0)";
      setTimeout(() => {
        // Reset inline styles before the sheet unmounts; otherwise
        // a future open inherits them.
        if (sheet) {
          sheet.style.transform  = "";
          sheet.style.transition = "";
        }
        if (overlay) overlay.style.background = "";
        store.closeEditor();
      }, 180);
    } else {
      snapBack();
    }
    reset();
  }

  sheet.addEventListener("touchstart",  onTouchStart, { passive: true });
  sheet.addEventListener("touchmove",   onTouchMove,  { passive: false });
  sheet.addEventListener("touchend",    onTouchEnd,   { passive: true });
  sheet.addEventListener("touchcancel", onTouchEnd,   { passive: true });
}

export function renderEditorSheet(state: AppState): TemplateResult {
  const convId = state.editingConversationId;
  if (!convId) {
    local = null;
    lastConvId = null;
    unlockBody();
    return html``;
  }
  const buyer = state.buyers.find((b) => b.conv_id === convId);
  if (!buyer) return html``;

  // First open: lock the underlying page and arm the swipe-down gesture
  // after the DOM commits.
  lockBody();
  setTimeout(mountSheetGestures, 0);

  // (Re)seed local state when a new conversation is opened.
  if (lastConvId !== convId) {
    lastConvId = convId;
    local = seedLocalFromBuyer(buyer);
    // Fire message load — fills L.messages async.
    void loadMessages(convId);
  }
  const L = local!;

  // Live thread refresh — when SSE/poll advances the buyer's
  // conv_last_activity_at past what we've seen, quietly reload the
  // messages list without showing a loading skeleton. This is what
  // makes a new inbound message appear in an OPEN sheet within a
  // second of the Worker's broadcastUpdate (FEST-PHASE-2.L).
  const serverActivity = buyer.conv_last_activity_at ?? 0;
  if (serverActivity > L.lastActivityKnown && !L.loadingMessages) {
    L.lastActivityKnown = serverActivity;
    void loadMessages(convId, { quiet: true });
  }

  const onTickets   = (e: Event) => updateField("tickets",   (e.target as HTMLInputElement).value);
  const onTotal     = (e: Event) => updateField("total",     (e.target as HTMLInputElement).value);
  const onPerTicket = (e: Event) => updateField("perTicket", (e.target as HTMLInputElement).value);
  const onReply     = (e: Event) => { if (local) { local.replyText = (e.target as HTMLTextAreaElement).value; repaint(); } };

  const charCount = L.replyText.length;
  const charLimit = 1000;
  const sendEnabled = L.replyText.trim().length > 0 && charCount <= charLimit && !L.sending;

  return html`
    <div class="sheet-overlay" @click=${overlayClick}>
      <div class="sheet" @click=${(e: Event) => e.stopPropagation()}>
        <header class="sheet__head">
          <div>
            <div class="sheet__title">${buyer.display_name}</div>
            <div class="sheet__sub">
              ${buyer.account_username ?? "—"} ·
              ${buyer.listing_title    ?? "—"}
              ${buyer.listing_price_cents != null ? ` · list ${dollars(buyer.listing_price_cents)}` : ""}
            </div>
          </div>
          <button class="sheet__close" type="button" aria-label="Close"
                  @click=${() => store.closeEditor()}>×</button>
        </header>

        <div class="sheet__body">
          <!-- 0. Buyer details (account + phone) -->
          ${renderBuyerDetails(L, state, buyer)}

          <!-- 1. Conversation thread -->
          <div class="sheet__section">
            <div class="sheet__label sheet__label--block">Thread</div>
            ${renderThread(L)}
          </div>

          <!-- 2. Reply composer -->
          <div class="sheet__section">
            <div class="sheet__label sheet__label--block">Reply</div>
            ${renderTemplateChips(state.templates)}
            <textarea class="sheet__input sheet__textarea"
                      placeholder="Type your reply to the buyer…"
                      rows="3"
                      maxlength="1100"
                      .value=${L.replyText}
                      @input=${onReply}
                      ?disabled=${L.sending}></textarea>
            <div class="sheet__row sheet__row--composer">
              <div class="sheet__hint">
                Goes out via Resend to <code>reply-${shortToken(buyer)}@messages.offerup.com</code>
                <span class="${charCount > charLimit ? "sheet__hint--error" : ""}">
                  · ${charCount}/${charLimit}
                </span>
                ${L.replyText.length > 0 ? html`
                  · <button class="sheet__clear-link" type="button"
                            @click=${clearReply}>Clear</button>
                ` : ""}
              </div>
              <button class="btn btn--reply" type="button"
                      ?disabled=${!sendEnabled}
                      @click=${sendReply}>
                ${L.sending ? "Sending…" : "Send reply"}
              </button>
            </div>
            ${L.sendErr ? html`<div class="sheet__err">${L.sendErr}</div>` : ""}
          </div>

          <!-- 3. Deal terms (three-way math) -->
          <div class="sheet__section">
            <div class="sheet__row">
              <label class="sheet__field">
                <span class="sheet__label">Tickets</span>
                <input class="sheet__input" inputmode="numeric" pattern="[0-9]*"
                       .value=${L.tickets} @input=${onTickets} />
              </label>
              <label class="sheet__field">
                <span class="sheet__label">Total</span>
                <span class="sheet__input-affix">
                  <span class="sheet__affix">$</span>
                  <input class="sheet__input" inputmode="decimal"
                         .value=${L.total} @input=${onTotal} />
                </span>
              </label>
              <label class="sheet__field">
                <span class="sheet__label">Per ticket</span>
                <span class="sheet__input-affix">
                  <span class="sheet__affix">$</span>
                  <input class="sheet__input" inputmode="decimal"
                         .value=${L.perTicket} @input=${onPerTicket} />
                </span>
              </label>
            </div>
            <div class="sheet__hint">
              Type any two — the third auto-derives.
              ${derivedNote(L)}
            </div>
          </div>

          <!-- 4. Status pills -->
          <div class="sheet__section">
            <div class="sheet__label sheet__label--block">Status</div>
            <div class="sheet__status-grid">
              ${ALL_STATUSES.filter((s) => s !== "paid").map((s) => html`
                <button type="button"
                        class="sheet__status pill pill--${s} ${L.status === s ? "sheet__status--active" : ""}"
                        @click=${() => { if (local) { local.status = s; repaint(); } }}>
                  ${s}
                </button>
              `)}
            </div>
            ${L.status === "paid" ? html`
              <div class="sheet__paid-hint">
                <span class="pill pill--paid">paid</span>
                Currently a closed sale. Use <strong>Reopen sale</strong> below to move
                this buyer back to the pipeline if the deal fell through.
              </div>
            ` : ""}
          </div>

          <!-- 5. Meet spot + time -->
          <div class="sheet__section">
            <div class="sheet__label sheet__label--block">Meet</div>
            <div class="sheet__row sheet__row--meet">
              <label class="sheet__field">
                <span class="sheet__label">Spot</span>
                <input class="sheet__input" type="text"
                       placeholder="Cosmo · festival · …"
                       .value=${L.meet}
                       @input=${(e: Event) => { if (local) { local.meet = (e.target as HTMLInputElement).value; repaint(); } }} />
              </label>
              <label class="sheet__field">
                <span class="sheet__label">Time</span>
                <input class="sheet__input" type="datetime-local"
                       .value=${L.meetAt}
                       @input=${(e: Event) => { if (local) { local.meetAt = (e.target as HTMLInputElement).value; repaint(); } }} />
              </label>
            </div>
            <div class="sheet__row sheet__row--composer">
              <div class="sheet__hint">
                Tip: type <code>Cosmo</code> or <code>festival</code> in Spot — the
                Tonight cube groups by those keywords.
              </div>
              <button class="btn btn--ghost btn--small" type="button"
                      @click=${suggestNextSlot}>
                Suggest next slot
              </button>
            </div>
            ${renderConflictWarning(L, state.buyers)}
          </div>

          ${L.err ? html`<div class="sheet__err">${L.err}</div>` : ""}
        </div>

        <footer class="sheet__foot">
          ${L.status === "paid"
            ? html`
                <button class="btn btn--reopen" type="button" @click=${reopenSale} ?disabled=${L.saving}>
                  Reopen sale
                </button>
              `
            : html`
                <button class="btn btn--paid" type="button" @click=${markPaid} ?disabled=${L.saving}>
                  Mark as Paid
                </button>
              `}
          <div class="sheet__spacer"></div>
          <button class="btn btn--ghost" type="button" @click=${() => store.closeEditor()} ?disabled=${L.saving}>
            Cancel
          </button>
          <button class="btn btn--primary" type="button" @click=${save} ?disabled=${L.saving}>
            ${L.saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  `;
}

/* ============== Buyer details (account + phone) ============== */

function renderBuyerDetails(L: LocalState, state: AppState, buyer: BuyerRow): TemplateResult {
  const detectedFromThread = detectPhoneInThread(L);
  const showDetectedHint   = detectedFromThread !== null && L.phone.trim() === "";
  // Account reassignment only matters when the auto-router couldn't
  // resolve. After 2.E.1's data cleanup this should virtually never
  // appear — but keep the affordance for any future auto-created
  // "(unknown)" accounts.
  const needsAccountFix    = buyer.account_username === "(unknown)";

  return html`
    <div class="sheet__section">
      ${needsAccountFix ? html`
        <div class="sheet__row sheet__row--meta">
          <label class="sheet__field">
            <span class="sheet__label">Account · pick to fix</span>
            <select class="sheet__input"
                    @change=${(e: Event) => {
                      if (local) { local.accountId = (e.target as HTMLSelectElement).value; repaint(); }
                    }}>
              ${state.accounts
                  .filter((a) => a.username !== "(unknown)")
                  .map((a) => html`
                    <option value=${a.id} ?selected=${a.id === L.accountId}>${a.username}</option>
                  `)}
            </select>
          </label>
        </div>
      ` : ""}
      <label class="sheet__field">
        <span class="sheet__label">Buyer phone</span>
        <span class="sheet__phone-row">
          <input class="sheet__input" type="tel"
                 placeholder="Add phone (optional)"
                 .value=${L.phone}
                 @input=${(e: Event) => { if (local) { local.phone = (e.target as HTMLInputElement).value; repaint(); } }} />
          ${renderPhoneActions(L.phone)}
        </span>
      </label>
      ${showDetectedHint ? html`
        <div class="sheet__detected">
          <span class="sheet__detected-text">
            <span class="sheet__detected-icon">${iconPhone()}</span>
            Detected in thread: <strong>${formatPhone(detectedFromThread!)}</strong>
          </span>
          <button class="btn btn--ghost btn--small" type="button"
                  @click=${() => { if (local) { local.phone = detectedFromThread!; repaint(); } }}>
            Use this
          </button>
        </div>
      ` : ""}
      <label class="sheet__field">
        <span class="sheet__label">Notes <span class="sheet__label-sub">(private — only you see these)</span></span>
        <textarea class="sheet__input sheet__textarea sheet__textarea--notes"
                  placeholder="Anything to remember about this buyer — pickup details, friend referral, prior festivals, etc."
                  rows="2"
                  .value=${L.notes}
                  @input=${(e: Event) => { if (local) { local.notes = (e.target as HTMLTextAreaElement).value; repaint(); } }}></textarea>
      </label>
    </div>
  `;
}

function renderPhoneActions(phone: string): TemplateResult {
  const normalized = normalizePhone(phone);
  if (!normalized) return html``;
  return html`
    <a class="sheet__phone-btn sheet__phone-btn--imessage"
       href=${imessageHref(normalized)}
       aria-label="Open in Messages"
       title="Open in Messages">
      ${iconMessage()}
    </a>
    <button class="sheet__phone-btn" type="button"
            aria-label="Copy phone number"
            title="Copy phone number"
            @click=${(e: Event) => copyToClipboard(normalized, e.currentTarget as HTMLElement)}>
      <span class="sheet__phone-btn-default">${iconClipboard()}</span>
      <span class="sheet__phone-btn-success">${iconCheck()}</span>
    </button>
  `;
}

function detectPhoneInThread(L: LocalState): string | null {
  for (const m of L.messages) {
    if (m.direction !== "inbound") continue;
    const phones = extractPhones(m.body);
    if (phones.length > 0) return phones[0]!;
  }
  return null;
}

function copyToClipboard(text: string, btn: HTMLElement): void {
  void navigator.clipboard.writeText(text).then(() => {
    btn.classList.add("sheet__phone-btn--copied");
    setTimeout(() => btn.classList.remove("sheet__phone-btn--copied"), 1200);
  });
}

/* ============== Template chips ============== */

import type { Template } from "../data/types";

function renderTemplateChips(templates: Template[]): TemplateResult {
  if (templates.length === 0) return html``;
  return html`
    <div class="tpl-chips">
      ${templates.map((t) => html`
        <button class="tpl-chip" type="button"
                title=${t.body}
                @click=${() => applyTemplate(t.body)}>
          ${t.label}
        </button>
      `)}
    </div>
  `;
}

function applyTemplate(body: string): void {
  if (!local) return;
  // If user already typed something, append with a newline; otherwise
  // replace. This preserves work-in-progress when they're combining.
  local.replyText = local.replyText.trim()
    ? `${local.replyText.trimEnd()}\n${body}`
    : body;
  repaint();
  // Focus the textarea so the user can immediately edit placeholders.
  setTimeout(() => {
    const ta = document.querySelector<HTMLTextAreaElement>(".sheet__textarea");
    if (ta) {
      ta.focus();
      // Place caret at first bracket placeholder if any, else at end.
      const placeholderMatch = ta.value.match(/\[[A-Z]+\]/);
      if (placeholderMatch && placeholderMatch.index !== undefined) {
        ta.setSelectionRange(placeholderMatch.index, placeholderMatch.index + placeholderMatch[0].length);
      } else {
        ta.setSelectionRange(ta.value.length, ta.value.length);
      }
    }
  }, 0);
}

/* ============== Thread rendering ============== */

function renderThread(L: LocalState): TemplateResult {
  if (L.loadingMessages && L.messages.length === 0) {
    return html`<div class="thread thread--loading"><div class="skeleton"></div><div class="skeleton"></div></div>`;
  }
  if (L.messages.length === 0) {
    return html`<div class="thread thread--empty">No messages yet.</div>`;
  }
  // FEST-PHASE-2.L.4 — render-time safety net for the phantom
  // "SENDING…" twin. The 2.L.3 merge absorbs optimistic outbounds
  // when the server-side copy lands, but if anything in the merge
  // misses (clock skew, body normalization, race), the UI still
  // shows two bubbles. We collapse them here: any two outbound
  // messages with the same body within 60s are treated as one, and
  // we prefer the server-side copy (no local_status) over the
  // optimistic temp.
  const visible = dedupeOutbound(L.messages);
  return html`
    <div class="thread">
      ${visible.map((m) => {
        const statusMod = m.local_status ? ` thread__msg--${m.local_status}` : "";
        return html`
          <div class="thread__msg thread__msg--${m.direction}${statusMod}">
            <div class="thread__bubble">${nl2br(m.body)}</div>
            <div class="thread__meta">
              ${m.local_status === "sending" ? html`<span class="thread__status-dot"></span> sending…`
                : m.local_status === "failed" ? html`
                    <span class="thread__failed-label">Not sent —</span>
                    <button class="thread__retry" type="button"
                            @click=${() => retrySend(m.local_temp_id!)}>retry</button>
                    <button class="thread__retry thread__retry--discard" type="button"
                            @click=${() => discardFailed(m.local_temp_id!)}>discard</button>
                  `
                : html`${m.direction === "inbound" ? "buyer" : "you"} · ${formatRelative(m.sent_at)}`}
            </div>
          </div>
        `;
      })}
    </div>
  `;
}

function nl2br(text: string): TemplateResult[] {
  const parts = text.split("\n");
  return parts.flatMap((p, i) =>
    i === 0 ? [html`${p}`] : [html`<br />`, html`${p}`]);
}

function shortToken(b: BuyerRow): string {
  // Display a friendly short form of the reply-token for the composer hint.
  // We don't have the token on BuyerRow directly; show a placeholder.
  return b.conv_id ? b.conv_id.replace(/^c_/, "").slice(0, 10) + "…" : "…";
}

/* ============== Data loading ============== */

async function loadMessages(convId: string, opts: { quiet?: boolean } = {}): Promise<void> {
  if (!local) return;
  if (!opts.quiet) {
    local.loadingMessages = true;
    local.messages        = [];
    repaint();
  }
  try {
    const msgs = await api.listMessages(convId);
    if (!local || lastConvId !== convId) return;
    // Preserve any in-flight optimistic outbound bubbles (sending or
    // failed) — UNLESS the server already absorbed them. This handles
    // the case where deliverReply's POST timed out client-side but
    // the server actually succeeded: the real message lands via this
    // reload, and we drop the stale optimistic ghost (FEST-PHASE-2.L.3).
    const localOnly = local.messages.filter((m) => {
      if (m.local_status !== "sending" && m.local_status !== "failed") return false;
      const absorbed = msgs.some((s) =>
        s.direction === "outbound" &&
        s.body === m.body &&
        Math.abs((s.sent_at ?? 0) - (m.sent_at ?? 0)) < 10_000,
      );
      return !absorbed;
    });
    local.messages        = [...msgs, ...localOnly];
    local.loadingMessages = false;
    repaint();
  } catch (e) {
    if (!local || lastConvId !== convId) return;
    local.loadingMessages = false;
    if (!opts.quiet) {
      local.sendErr = `Couldn't load thread: ${e instanceof Error ? e.message : String(e)}`;
    }
    repaint();
  }
}

function clearReply(): void {
  if (!local) return;
  local.replyText = "";
  local.sendErr   = null;
  repaint();
  // Return focus to the textarea so the operator can keep typing.
  setTimeout(() => {
    document.querySelector<HTMLTextAreaElement>(".sheet__textarea")?.focus();
  }, 0);
}

async function sendReply(): Promise<void> {
  if (!local) return;
  const convId = lastConvId;
  if (!convId) return;
  const text = local.replyText.trim();
  if (!text) return;
  if (text.length > 1000) {
    local.sendErr = "Reply exceeds OfferUp's 1000-char limit.";
    repaint();
    return;
  }
  // Optimistic-first send (FEST-PHASE-2.L). The bubble lands in the
  // thread before the network call resolves, so flaky connections
  // can't leave the operator staring at a stuck "Sending…" button.
  // If the POST fails we mark the bubble for retry; if it succeeds we
  // upgrade the temp id to the server's. Either way the textarea is
  // cleared and the composer is immediately usable again.
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  local.messages.push({
    id:              tempId,
    conversation_id: convId,
    direction:       "outbound",
    body:            text,
    raw_email_id:    null,
    sent_at:         Date.now(),
    local_status:    "sending",
    local_temp_id:   tempId,
  });
  local.replyText = "";
  local.sending   = false;
  local.sendErr   = null;
  repaint();
  scrollThreadToBottom();
  void deliverReply(convId, text, tempId);
}

async function deliverReply(convId: string, text: string, tempId: string): Promise<void> {
  try {
    // tempId is the idempotency key — stable across iOS POST retries
    // because the retry replays the SAME HTTP request including this
    // header. Server dedupes by this key (FEST-PHASE-2.Z).
    const resp = await api.postReply(convId, text, tempId);
    if (!local || lastConvId !== convId) return;
    const i = local.messages.findIndex((m) => m.local_temp_id === tempId);
    if (i >= 0) {
      // Replace optimistic bubble with the server's authoritative copy.
      local.messages[i] = {
        id:              resp.message_id,
        conversation_id: convId,
        direction:       "outbound",
        body:            text,
        raw_email_id:    `resend:${resp.resend_message_id}`,
        sent_at:         resp.sent_at,
      };
      repaint();
    }
  } catch (e) {
    if (!local || lastConvId !== convId) return;
    const i = local.messages.findIndex((m) => m.local_temp_id === tempId);
    if (i >= 0) {
      const msg = local.messages[i]!;
      local.messages[i] = { ...msg, local_status: "failed" };
      repaint();
    }
    // eslint-disable-next-line no-console
    console.warn("sendReply error:", e);
  }
}

function retrySend(tempId: string): void {
  if (!local) return;
  const convId = lastConvId;
  if (!convId) return;
  const i = local.messages.findIndex((m) => m.local_temp_id === tempId);
  if (i < 0) return;
  const msg = local.messages[i]!;
  local.messages[i] = { ...msg, local_status: "sending" };
  repaint();
  void deliverReply(convId, msg.body, tempId);
}

function discardFailed(tempId: string): void {
  if (!local) return;
  local.messages = local.messages.filter((m) => m.local_temp_id !== tempId);
  repaint();
}

function scrollThreadToBottom(): void {
  // Defer so the new bubble is in the DOM before we measure.
  setTimeout(() => {
    const thread = document.querySelector<HTMLElement>("#editor-sheet-host .thread");
    if (!thread) return;
    thread.scrollTop = thread.scrollHeight;
  }, 0);
}

/* ============== Numeric math (unchanged from 1.0.B) ============== */

function seedLocalFromBuyer(b: BuyerRow): LocalState {
  const tickets   = b.conv_tickets_count       != null ? String(b.conv_tickets_count) : "";
  const totalNum  = b.conv_agreed_price_cents  != null ? b.conv_agreed_price_cents / 100 : null;
  const perNum    = b.conv_tickets_count && b.conv_agreed_price_cents != null
                      ? (b.conv_agreed_price_cents / 100) / b.conv_tickets_count
                      : null;
  return {
    tickets,
    total:           totalNum != null ? totalNum.toFixed(2).replace(/\.00$/, "") : "",
    perTicket:       perNum   != null ? perNum.toFixed(2).replace(/\.00$/, "")   : "",
    status:          b.conv_status ?? "inquired",
    meet:            b.conv_agreed_meet_spot ?? "",
    meetAt:          b.conv_agreed_meet_at != null ? msToInputLocal(b.conv_agreed_meet_at) : "",
    accountId:       b.account_id ?? "",
    phone:           b.phone ?? "",
    notes:           b.notes ?? "",
    history:         tickets ? ["tickets", totalNum != null ? "total" : "perTicket"] : [],
    saving:          false,
    err:             null,
    lastActivityKnown: b.conv_last_activity_at ?? 0,
    messages:        [],
    loadingMessages: true,
    replyText:       "",
    sending:         false,
    sendErr:         null,
  };
}

/* ============== Meet-time helpers ============== */

/** epoch ms → "YYYY-MM-DDTHH:mm" in local time for <input type="datetime-local">. */
function msToInputLocal(ms: number): string {
  const d   = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "YYYY-MM-DDTHH:mm" (local) → epoch ms. Returns null on empty/invalid. */
function inputLocalToMs(s: string): number | null {
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Pick the next free slot at the user's chosen venue. Heuristic:
 *   - Classify the typed Spot to Cosmo / Festival / Other (case-insensitive
 *     substring match — same logic as the schedule cube).
 *   - Among other conversations at the same venue, find the latest
 *     agreed_meet_at. Add 15 minutes → that's our suggestion.
 *   - If no existing meets at that venue, start at "now + 30 min" rounded
 *     up to the next 15-minute mark — gives Yury a bit of headroom from
 *     the moment of typing.
 */
function suggestNextSlot(): void {
  if (!local) return;
  const venue   = classifyVenueText(local.meet);
  const buyers  = store.state.buyers;
  let latest    = 0;
  for (const b of buyers) {
    if (b.conv_id === store.state.editingConversationId) continue;  // skip self
    if (b.conv_agreed_meet_at == null) continue;
    if (b.conv_status === "ghosted" || b.conv_status === "declined") continue;
    const otherVenue = classifyVenueText(b.conv_agreed_meet_spot);
    if (otherVenue !== venue) continue;
    if (b.conv_agreed_meet_at > latest) latest = b.conv_agreed_meet_at;
  }
  const base = latest > 0 ? latest + 15 * 60 * 1000 : Date.now() + 30 * 60 * 1000;
  const rounded = roundUpToQuarterHour(base);
  local.meetAt = msToInputLocal(rounded);
  repaint();
}

function classifyVenueText(spot: string | null | undefined): "Cosmo" | "Festival" | "Other" {
  // Mirror of schedule.classifyVenue, but tolerates undefined.
  return classifyVenue(spot ?? null);
}

/** Inline conflict banner: shows when the user's chosen meet time
 *  collides with another buyer's meet at the same venue. */
function renderConflictWarning(L: LocalState, buyers: BuyerRow[]): TemplateResult {
  const ms = inputLocalToMs(L.meetAt);
  if (ms == null) return html``;
  const venue = classifyVenueText(L.meet);
  const conflictName = findConflictFor(
    buyers, venue, ms, store.state.editingConversationId,
  );
  if (!conflictName) return html``;
  return html`
    <div class="sheet__warn">
      ⚠ Within 5 min of <strong>${conflictName}</strong> at the same venue.
      Tap <em>Suggest next slot</em> to push to the next free time.
    </div>
  `;
}

function roundUpToQuarterHour(ms: number): number {
  const d  = new Date(ms);
  const m  = d.getMinutes();
  const rem = m % 15;
  if (rem === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) return ms;
  d.setMinutes(m + (15 - rem));
  d.setSeconds(0, 0);
  return d.getTime();
}

function updateField(field: NumField, raw: string): void {
  if (!local) return;
  local[field] = raw;
  promote(field);
  recompute();
  repaint();
}

function promote(field: NumField): void {
  if (!local) return;
  local.history = [field, ...local.history.filter((f) => f !== field)];
}

function recompute(): void {
  if (!local) return;
  const [a, b] = local.history;
  if (!a || !b) return;
  const all: NumField[] = ["tickets", "total", "perTicket"];
  const derived = all.find((f) => f !== a && f !== b);
  if (!derived) return;

  const tickets = parseFloat(local.tickets);
  const total   = parseFloat(local.total);
  const perTk   = parseFloat(local.perTicket);

  if (derived === "tickets" && Number.isFinite(total) && Number.isFinite(perTk) && perTk > 0) {
    const v = total / perTk;
    local.tickets = Number.isFinite(v) ? String(round(v)) : "";
  } else if (derived === "total" && Number.isFinite(tickets) && Number.isFinite(perTk)) {
    const v = tickets * perTk;
    local.total = Number.isFinite(v) ? fmt(v) : "";
  } else if (derived === "perTicket" && Number.isFinite(tickets) && tickets > 0 && Number.isFinite(total)) {
    const v = total / tickets;
    local.perTicket = Number.isFinite(v) ? fmt(v) : "";
  }
}

function derivedNote(L: LocalState): TemplateResult {
  const [a, b] = L.history;
  if (!a || !b) return html``;
  const all: NumField[] = ["tickets", "total", "perTicket"];
  const derived = all.find((f) => f !== a && f !== b);
  if (!derived) return html``;
  return html` <span class="sheet__hint-derived">→ ${labelFor(derived)} auto-derived.</span>`;
}

function labelFor(f: NumField): string {
  return f === "tickets" ? "Tickets" : f === "total" ? "Total" : "Per ticket";
}

function fmt(n: number): string {
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return (Math.round(n * 100) / 100).toFixed(2).replace(/\.?0+$/, "");
}
function round(n: number): number { return Math.round(n); }

function dollars(cents: number): string { return `$${(cents / 100).toFixed(0)}`; }

/* ============== Actions ============== */

function save(): void {
  if (!local) return;
  // Same fire-and-forget pattern as markPaid / reopenSale (2.Q.1).
  // patchEditingConversation updates the local buyers slice and
  // emits synchronously before awaiting the network. Buyer phone
  // and notes are also pushed optimistically inside saveBuyerIfChanged.
  // The sheet closes instantly; the network resolves in the
  // background and store.refresh() reconciles on any failure.
  void store.patchEditingConversation(buildPatch(local));
  void saveBuyerIfChanged();
  store.closeEditor();
}

function markPaid(): void {
  if (!local) return;
  // Optimistic + fire-and-forget (FEST-PHASE-2.Q.1). The store's
  // patchEditingConversation updates the local buyers slice and emits
  // synchronously before awaiting the network, so the closed-sales
  // cube picks up the moved buyer the instant the sheet closes. Any
  // network error triggers store.refresh() to reconcile.
  local.status = "paid";
  void store.patchEditingConversation({ ...buildPatch(local), status: "paid" });
  void saveBuyerIfChanged();
  store.closeEditor();
}

/** Reverse of markPaid (FEST-PHASE-2.Q). Sale fell through / hit by
 *  accident → flip status back to "committed". Same fire-and-forget
 *  pattern as markPaid — no "Saving…" stall, sheet vanishes instantly. */
function reopenSale(): void {
  if (!local) return;
  if (!confirm("Reopen this sale? The buyer will move back to the active pipeline as 'committed'.")) return;
  local.status = "committed";
  void store.patchEditingConversation({ ...buildPatch(local), status: "committed" });
  void saveBuyerIfChanged();
  store.closeEditor();
}

/** Phone + notes live on buyer (not conversation). Save them together
 *  whenever either has changed. Phone gets normalized to E.164 when
 *  possible. */
async function saveBuyerIfChanged(): Promise<void> {
  if (!local) return;
  const buyer = store.state.buyers.find(
    (b) => b.conv_id === store.state.editingConversationId,
  );
  if (!buyer) return;

  const patch: { phone?: string | null; notes?: string | null } = {};

  const currentPhone = buyer.phone ?? "";
  const trimmedPhone = local.phone.trim();
  if (trimmedPhone !== currentPhone) {
    const normalized = normalizePhone(trimmedPhone);
    patch.phone = normalized ?? trimmedPhone ?? null;
    if (patch.phone === "") patch.phone = null;
  }

  const currentNotes = buyer.notes ?? "";
  const trimmedNotes = local.notes.trim();
  if (trimmedNotes !== currentNotes) {
    patch.notes = trimmedNotes || null;
  }

  if (Object.keys(patch).length === 0) return;
  // Optimistic update so re-opening this buyer right away shows the
  // new phone/notes without waiting on the network.
  store.optimisticPatchBuyer(buyer.id, patch);
  await api.patchBuyer(buyer.id, patch);
}

function buildPatch(L: LocalState): import("../data/types").PatchConversationBody {
  const tickets = L.tickets === "" ? null : parseInt(L.tickets, 10);
  const total   = L.total   === "" ? null : Math.round(parseFloat(L.total) * 100);
  const meetAt  = inputLocalToMs(L.meetAt);
  return {
    tickets_count:      Number.isFinite(tickets as number) ? (tickets as number | null) : null,
    agreed_price_cents: Number.isFinite(total as number)   ? (total   as number | null) : null,
    status:             L.status,
    agreed_meet_spot:   L.meet.trim() || null,
    agreed_meet_at:     meetAt,
    ...(L.accountId ? { account_id: L.accountId } : {}),
  };
}

function overlayClick(): void { store.closeEditor(); }

function repaint(): void {
  const host = document.getElementById("editor-sheet-host");
  if (host) render(renderEditorSheet(store.state), host);
}

/* ============== Reply-focus jump fix (FEST-PHASE-2.L) ==============
 * On iOS Safari the reply textarea sits mid-way through the sheet body.
 * When it gains focus, the browser scrolls it into view — and lands it
 * at the TOP of the visual viewport, hiding the entire thread above it.
 * Operators expect a chat UI: textarea just above the keyboard, thread
 * still readable above. We override the default by waiting for the
 * keyboard to actually open (visualViewport.resize fires), measuring
 * the textarea, and scrolling the sheet body so the textarea sits at
 * the bottom of the visible area with a small breathing gap. */
function mountReplyFocusFix(scrollEl: HTMLElement): void {
  if (!("ontouchstart" in window)) return;
  const sheet = scrollEl.closest<HTMLElement>(".sheet");
  if (!sheet) return;
  const vv = window.visualViewport;
  let pendingTextarea: HTMLTextAreaElement | null = null;
  let blurTimer: number | null = null;

  function isTypingTarget(t: EventTarget | null): boolean {
    if (!(t instanceof HTMLElement)) return false;
    const tag = t.tagName;
    return tag === "TEXTAREA" || (tag === "INPUT" && (t as HTMLInputElement).type !== "checkbox");
  }

  function setKeyboardOpen(open: boolean): void {
    sheet!.classList.toggle("sheet--keyboard-open", open);
  }

  function position(ta: HTMLTextAreaElement): void {
    // We want the Send button + char-count hint visible too, not just
    // the textarea. Target the composer row that sits just below the
    // textarea (Hint + Send), or the textarea itself if that row isn't
    // present (e.g. focus came from the Notes textarea).
    const composer = ta.parentElement?.querySelector<HTMLElement>(".sheet__row--composer");
    const target = composer ?? ta;
    const rect = target.getBoundingClientRect();
    const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const desiredBottom = visibleBottom - 12;
    const delta = rect.bottom - desiredBottom;
    if (Math.abs(delta) > 4) scrollEl.scrollTop += delta;
  }

  scrollEl.addEventListener("focusin", (e) => {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (!isTypingTarget(e.target)) return;
    // Any text-typing input means the keyboard is up — hide the footer.
    // This avoids relying on visualViewport math which is iOS-version
    // dependent (and inconsistent under position:fixed body lock).
    setKeyboardOpen(true);
    const t = e.target;
    if (t instanceof HTMLTextAreaElement && t.classList.contains("sheet__textarea")) {
      pendingTextarea = t;
      // Two scroll nudges — once partway through the keyboard slide,
      // once when it has settled.
      setTimeout(() => position(t), 160);
      setTimeout(() => position(t), 360);
    }
  }, { passive: true });

  scrollEl.addEventListener("focusout", (e) => {
    if (!isTypingTarget(e.target)) return;
    pendingTextarea = null;
    // Delay before hiding, so iOS shifting focus between two inputs
    // doesn't flicker the footer on/off.
    blurTimer = window.setTimeout(() => {
      const active = document.activeElement;
      if (!isTypingTarget(active) || !scrollEl.contains(active)) {
        setKeyboardOpen(false);
      }
      blurTimer = null;
    }, 180);
  }, { passive: true });

  vv?.addEventListener("resize", () => {
    if (pendingTextarea) position(pendingTextarea);
  });
}

/** Collapse outbound twins (FEST-PHASE-2.L.4).
 *  Keeps the server-side copy when both a server message and an
 *  optimistic temp with the same body exist within 60 seconds.
 *  Pure UI-side defense — the 2.L.3 merge is the primary mechanism.
 */
function dedupeOutbound(msgs: Message[]): Message[] {
  const out: Message[] = [];
  for (const m of msgs) {
    if (m.direction !== "outbound") { out.push(m); continue; }
    const twinIdx = out.findIndex((s) =>
      s.direction === "outbound" &&
      s.body === m.body &&
      Math.abs((s.sent_at ?? 0) - (m.sent_at ?? 0)) < 60_000,
    );
    if (twinIdx === -1) { out.push(m); continue; }
    const existing = out[twinIdx]!;
    // Prefer the server-side copy (no local_status) over the temp.
    if (existing.local_status && !m.local_status) {
      out[twinIdx] = m;
    }
    // Else (existing is server or both are local/server) drop m.
  }
  return out;
}
